import React from 'react';
import { useChartAccessorsContext } from '../../ChartRoot';
import { TableLegendRowSlotProps } from './TableLegend.types';
import { TableLegendCells } from './TableLegendCells';
import { TableLegendRoot, TableLegendRootProps } from './TableLegendRoot';
import { TableLegendRow } from './TableLegendRow';
import { useTableLegendRow } from './useTableLegendRow';

export type TableLegendSlotProps<D> = {
  root?: TableLegendRootProps;
  row?: TableLegendRowSlotProps<D>;
};

type TableLegendProps<D> = {
  data: D[];
  total?: number;
  slotProps?: TableLegendSlotProps<D>;
};

const RowChildren = <D,>({ children }: { children: TableLegendRowSlotProps<D>['children'] | undefined }) => {
  const ctx = useTableLegendRow<D>();
  if (children === undefined) return <TableLegendCells />;
  if (typeof children === 'function') return children(ctx);
  return children;
};

export const TableLegend = <D,>({ data, total: totalOverride, slotProps }: TableLegendProps<D>) => {
  const { getValue, getIdentifier } = useChartAccessorsContext();
  const total = totalOverride ?? data.reduce((acc: number, d) => acc + getValue(d), 0);

  return (
    <TableLegendRoot {...slotProps?.root}>
      {data.map((datum, index) => (
        <TableLegendRow key={getIdentifier(datum)} datum={datum} index={index} total={total}>
          <RowChildren>{slotProps?.row?.children}</RowChildren>
        </TableLegendRow>
      ))}
    </TableLegendRoot>
  );
};
